/**
 * Single shared auth state via React Context.
 * (Previously each useAuth() call had its own useState — logout in a layout
 * did not update App / ProtectedRoute, causing redirect loops.)
 */
import { authApi } from '@/api/auth';
import { usersApi } from '@/api/users';
import { TokenResponse, UserMe } from '@/types';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  return null;
}

export type AuthContextValue = {
  user: UserMe | null;
  tokens: TokenResponse | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (userData: UserMe, tokenData: TokenResponse) => void;
  logout: () => Promise<void>;
  updateUser: (userData: UserMe) => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserMe | null>(null);
  const [tokens, setTokens] = useState<TokenResponse | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const clearAuth = useCallback(() => {
    setUser(null);
    setTokens(null);
    setIsAuthenticated(false);
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  }, []);

  useEffect(() => {
    let cancelled = false;

    const initializeAuth = async () => {
      try {
        const accessToken =
          getCookie('access_token') || localStorage.getItem('access_token');
        const refreshToken =
          getCookie('refresh_token') || localStorage.getItem('refresh_token');

        if (accessToken && refreshToken) {
          const tokenData: TokenResponse = {
            access_token: accessToken,
            refresh_token: refreshToken,
            token_type: 'bearer',
            expires_in: 3600,
          };
          if (!cancelled) {
            setTokens(tokenData);
          }

          try {
            const userProfile = await usersApi.getProfile();
            if (cancelled) {
              return;
            }
            setUser(userProfile);
            setIsAuthenticated(true);

            const updatedAccessToken =
              getCookie('access_token') || localStorage.getItem('access_token');
            const updatedRefreshToken =
              getCookie('refresh_token') || localStorage.getItem('refresh_token');
            if (updatedAccessToken && updatedRefreshToken) {
              setTokens({
                access_token: updatedAccessToken,
                refresh_token: updatedRefreshToken,
                token_type: 'bearer',
                expires_in: 3600,
              });
            }
          } catch (error: unknown) {
            console.error('Failed to load user profile:', error);

            const stillHasTokens =
              (getCookie('access_token') || localStorage.getItem('access_token')) &&
              (getCookie('refresh_token') || localStorage.getItem('refresh_token'));

            if (cancelled) {
              return;
            }
            if (!stillHasTokens) {
              setUser(null);
              setTokens(null);
              setIsAuthenticated(false);
            } else {
              clearAuth();
            }
          }
        } else if (!cancelled) {
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        const hasTokens =
          (getCookie('access_token') || localStorage.getItem('access_token')) &&
          (getCookie('refresh_token') || localStorage.getItem('refresh_token'));
        if (!cancelled && !hasTokens) {
          setIsAuthenticated(false);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void initializeAuth();
    return () => {
      cancelled = true;
    };
  }, [clearAuth]);

  const login = useCallback((userData: UserMe, tokenData: TokenResponse) => {
    setUser(userData);
    setTokens(tokenData);
    setIsAuthenticated(true);
    localStorage.setItem('access_token', tokenData.access_token);
    localStorage.setItem('refresh_token', tokenData.refresh_token);
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // Server may be unreachable; still clear client session
    }
    clearAuth();
  }, [clearAuth]);

  const updateUser = useCallback((userData: UserMe) => {
    setUser(userData);
  }, []);

  const value = useMemo(
    () => ({
      user,
      tokens,
      isAuthenticated,
      isLoading,
      login,
      logout,
      updateUser,
    }),
    [user, tokens, isAuthenticated, isLoading, login, logout, updateUser]
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
